import { Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { FormChangeEvent, TechnicianData } from './technicianModal';
import { DateField } from '../../../pages/Booking/Details';
import { ImageUpload } from '../../atoms/UploadImage';
import { RadioButtonGroup } from '../../molecules/RadioGroup';

interface BasicFormProps {
  formData: TechnicianData;
  handleChange: (e: FormChangeEvent) => void;
  onImageChange: (url: string) => void;
  onRemoveImage: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onPhoneBlur?: () => void;
  phoneError?: string;
  checkingPhone?: boolean;
}

export const BasicForm: React.FC<BasicFormProps> = ({
  formData,
  handleChange,
  onImageChange,
  onRemoveImage,
  setIsLoading,
  onPhoneBlur,
  phoneError,
  checkingPhone,
}) => {
  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography gutterBottom>
            {' '}
            Full Name <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <TextField
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>
            {' '}
            Contact Number <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <TextField
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={onPhoneBlur}
            fullWidth
            required
            autoComplete="off"
            error={Boolean(phoneError)}
            inputProps={{ maxLength: 10, pattern: '[0-9]*' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#222',
                borderRadius: '8px',
                color: '#fff',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+44</InputAdornment>
              ),
            }}
          />
          {/* Rendered outside MUI's helperText so it never inherits the
              input's own background/border box — just a plain status line. */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              minHeight: '1.2em',
              mt: 0.5,
              color: phoneError ? '#f44336' : '#9e9e9e',
              backgroundColor: 'transparent',
            }}
          >
            {checkingPhone ? 'Checking number…' : phoneError || ' '}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>Email Address</Typography>
          <TextField
            name="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>
            Date of Birth <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <DateField
            name="dob"
            type="date"
            value={
              formData?.dob && !isNaN(new Date(formData.dob).getTime())
                ? new Date(formData.dob).toISOString().split('T')[0]
                : ''
            }
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>
            Type of Technician <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <RadioButtonGroup
            name="type"
            value={formData.type}
            options={['ACD', 'FC']}
            optionLabels={{ ACD: 'NVK' }}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>Joining Date</Typography>
          <DateField
            name="joiningDate"
            type="date"
            value={
              formData?.joiningDate &&
              !isNaN(new Date(formData.joiningDate).getTime())
                ? new Date(formData.joiningDate).toISOString().split('T')[0]
                : ''
            }
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item xs={6} lg={6} md={6}>
          <Typography gutterBottom>Technician Photo</Typography>
          <ImageUpload
            imageUrl={formData.image}
            onChange={onImageChange}
            onRemove={onRemoveImage}
            setLoader={setIsLoading}
            uploadLabel="Upload Photo"
            fileId="technician-image-upload"
            type="profile"
            disabled={false}
            folderType={5}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom mt={1}>
            {' '}
            Secondary Contact Number
          </Typography>
          <TextField
            name="secondaryContactNumber"
            value={formData.secondaryContactNumber}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 10, pattern: '[0-9]*' }}
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+44</InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>House</Typography>
          <TextField
            name="house"
            value={formData.house}
            onChange={handleChange}
            fullWidth
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>
            Street <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <TextField
            name="street"
            value={formData.street}
            onChange={handleChange}
            fullWidth
            required
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>
            City <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <TextField
            name="city"
            value={formData.city}
            onChange={handleChange}
            fullWidth
            required
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>
            State <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <TextField
            name="state"
            value={formData.state}
            onChange={handleChange}
            fullWidth
            required
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>
            Zipcode <span style={{ color: '#7B1FA2' }}>*</span>
          </Typography>
          <TextField
            name="zipcode"
            value={formData.zipcode}
            onChange={handleChange}
            fullWidth
            required
            inputProps={{ maxLength: 7, pattern: '[A-Za-z0-9]*' }}
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography gutterBottom>Landmark</Typography>
          <TextField
            name="landmark"
            value={formData.landmark}
            onChange={handleChange}
            fullWidth
            sx={{
              backgroundColor: '#222',
              borderRadius: '8px',
              color: '#fff',
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
